import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import EntityList from "src/components/hallinta/EntityList";
import EntityFormDialog from "src/components/hallinta/EntityFormDialog";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import * as api from "src/utils/hallinta/hallintaApi";
import { captureError } from "src/utils/errorReporting";

const jerryActive = (until) => until != null && new Date(until) >= new Date();

// Search field config for the shared EntityList controls (module constant, no
// copy). "__typeName" is a derived field (resolved human type label) added to
// each row below, so the "Tyyppi" sort/search works on the label rather than
// the raw typeId.
const SIJAINTI_SEARCH_KEYS = ["sijaintiNimi", "sijaintiOsoite1", "sijaintiOsoite2", "__typeName"];

// Sort-option labels are language-bearing, so this is a function of `t`
// (see VehiclesTab.jsx's vehicleSortOptions for the same pattern).
function sijaintiSortOptions(t) {
  return [
    { key: "sijaintiNimi", label: t("provider.sijainnit.nameLabel") },
    { key: "__typeName", label: t("provider.sijainnit.typeLabel") },
  ];
}

export default function SijainnitTab({ company, canEdit }) {
  const { token } = useAuth();
  const t = useT();
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState([]);
  const [error, setError] = useState(false);
  const [editRow, setEditRow] = useState(undefined);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [list, t] = await Promise.all([
        api.listSijainnit({ token, asiakasId: company.asiakasId }),
        api.listSijaintiTypes({ token, useJerry: true }),
      ]);
      // Hide deactivated (soft-deleted) and expired sijainnit from the list.
      // endDate is a SQL date (UTC midnight); compare against end-of-day so a
      // location stays visible through its whole final local calendar day.
      const now = new Date();
      const endOfDay = (d) => {
        const x = new Date(d);
        x.setHours(23, 59, 59, 999);
        return x;
      };
      // `t` is fetched useJerry=1, so its typeIds are the DB-driven allow-list.
      // Empty `t` (soft-failed types call) skips the filter rather than blank the list.
      const jerryTypeIds = new Set((t || []).map((x) => x.sijaintiTypeId));
      const visible = (list || []).filter(
        (s) =>
          s.deletedTime == null &&
          (!s.endDate || endOfDay(s.endDate) >= now) &&
          (jerryTypeIds.size === 0 || jerryTypeIds.has(s.sijaintiTypeId))
      );
      setRows(visible.map((s) => ({ ...s, __key: s.sijaintiId })));
      setTypes(t || []);
    } catch (err) {
      captureError(err, { tags: { entity: "sijainti", operation: "list" } });
      setError(true);
      setRows([]);
    }
  }, [token, company.asiakasId]);

  useEffect(() => {
    (async () => {
      await load();
    })();
  }, [load]);

  const schema = useMemo(
    () => [
      { key: "sijaintiNimi", label: t("provider.sijainnit.nameLabel"), type: "text", required: true },
      {
        key: "sijaintiOsoite1",
        label: t("provider.sijainnit.streetLabel"),
        type: "address",
        required: true,
        postalCityKey: "sijaintiOsoite2",
        latKey: "lat",
        lngKey: "lng",
        placeIdKey: "placeId",
      },
      { key: "sijaintiOsoite2", label: t("provider.sijainnit.postalCityLabel"), type: "text" },
      {
        key: "sijaintiTypeId",
        label: t("provider.sijainnit.typeLabel"),
        type: "select",
        required: true,
        options: types.map((st) => ({
          value: st.sijaintiTypeId,
          label: st.sijaintiTypeSelite,
        })),
      },
      {
        key: "jerryActive",
        label: t("provider.sijainnit.jerryActiveLabel"),
        type: "switch",
        default: true,
        help: t("provider.sijainnit.jerryActiveHelp"),
      },
      {
        key: "maxDeliveryDistance",
        label: t("provider.sijainnit.deliveryDistanceLabel"),
        type: "number",
        default: 75,
        min: 0,
        visibleWhen: (f) => f.jerryActive === true,
        help: t("provider.sijainnit.deliveryDistanceHelp"),
      },
      {
        key: "puomiMin",
        label: t("provider.sijainnit.puomiMinLabel"),
        type: "number",
        min: 0,
        half: true,
        visibleWhen: (f) => f.jerryActive === true,
        // fb#415: puomiMin is stored but NOT used for matching — any pump runs
        // as a line pump without the boom, so a minimum would only exclude you
        // from work you can do. Don't restore the old "välillä min–max" copy —
        // it now lives in the i18n bundles, so the same rule applies there.
        help: t("provider.sijainnit.puomiMinHelp"),
      },
      {
        key: "puomiMax",
        label: t("provider.sijainnit.puomiMaxLabel"),
        type: "number",
        min: 0,
        half: true,
        visibleWhen: (f) => f.jerryActive === true,
        // puomiMax IS the matching bound (fb#415) — this text carries the real rule.
        help: t("provider.sijainnit.puomiMaxHelp"),
      },
    ],
    [types, t]
  );

  async function submit(form) {
    const base = {
      sijaintiNimi: form.sijaintiNimi,
      sijaintiOsoite1: form.sijaintiOsoite1 || null,
      sijaintiOsoite2: form.sijaintiOsoite2 || null,
      sijaintiTypeId: Number(form.sijaintiTypeId),
      jerryActiveUntil: form.jerryActive ? "9999-12-31T00:00:00" : null,
    };
    const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
    if (form.jerryActive) {
      base.maxDeliveryDistance = Number(form.maxDeliveryDistance || 75);
      // Boom range (m): null = unbounded. Only sent while Jerry is on; on the
      // edit path the spread of the fetched row below preserves a stored range
      // when Jerry is toggled off.
      base.puomiMin = numOrNull(form.puomiMin);
      base.puomiMax = numOrNull(form.puomiMax);
    }

    // addSijainti/updateSijainti don't persist coordinates — when the address
    // picker captured GPS, write it via the dedicated updateLatLng endpoint.
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    const hasCoords =
      form.lat != null && form.lng != null && Number.isFinite(lat) && Number.isFinite(lng);

    let sijaintiId;
    if (editRow) {
      const { __key: _k, jerryActive: _ja, ...rest } = editRow;
      sijaintiId = editRow.sijaintiId;
      await api.updateSijainti({ token, fields: { ...rest, sijaintiId, ...base } });
    } else {
      const created = await api.addSijainti({ token, fields: { asiakasId: company.asiakasId, ...base } });
      sijaintiId = created?.sijaintiId;
    }
    if (hasCoords && sijaintiId) {
      // Best-effort: coords are secondary to the save. A failed geocode write
      // must not present the (successful) sijainti save as failed.
      try {
        await api.updateSijaintiLatLng({ token, sijaintiId, lat, lng });
      } catch (err) {
        captureError(err, { tags: { entity: "sijainti", operation: "updateLatLng" }, extra: { sijaintiId } });
      }
    }
    await load();
  }

  const typeName = (id) =>
    types.find((st) => st.sijaintiTypeId === id)?.sijaintiTypeSelite || "";

  // Decorate rows with the resolved type label so the "Tyyppi" sort/search has
  // a value to work on (the raw row only carries sijaintiTypeId).
  const decoratedRows = useMemo(
    () => rows?.map((s) => ({ ...s, __typeName: typeName(s.sijaintiTypeId) })) ?? rows,
    [rows, types] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box>
      <EntityList
        title={t("provider.sijainnit.heading")}
        rows={decoratedRows}
        loading={rows === null}
        error={error}
        canEdit={canEdit}
        entityTag="sijainti"
        searchKeys={SIJAINTI_SEARCH_KEYS}
        sortOptions={sijaintiSortOptions(t)}
        onAdd={() => setEditRow(null)}
        onEdit={(r) => setEditRow({ ...r, jerryActive: jerryActive(r.jerryActiveUntil) })}
        onDelete={async (r) => {
          await api.deleteSijainti({ token, sijaintiId: r.sijaintiId });
          await load();
        }}
        renderRow={(s) => {
          const active = jerryActive(s.jerryActiveUntil);
          const activeLabel = active ? t("provider.sijainnit.jerryActiveLabel") : t("provider.sijainnit.jerryInactiveLabel");
          return (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {s.sijaintiNimi}
                </Typography>
                <BoltIcon
                  fontSize="small"
                  color={active ? "primary" : "disabled"}
                  aria-label={activeLabel}
                  titleAccess={activeLabel}
                />
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {[s.sijaintiOsoite1, s.sijaintiOsoite2, typeName(s.sijaintiTypeId)]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          );
        }}
      />
      {editRow !== undefined && (
        <EntityFormDialog
          open
          onClose={() => setEditRow(undefined)}
          title={editRow ? t("provider.sijainnit.editDialogTitle") : t("provider.sijainnit.addDialogTitle")}
          schema={schema}
          row={editRow}
          entityTag="sijainti"
          onSubmit={submit}
          validateForm={(f) =>
            f.jerryActive === true &&
            f.puomiMin !== "" && f.puomiMin != null &&
            f.puomiMax !== "" && f.puomiMax != null &&
            Number(f.puomiMin) > Number(f.puomiMax)
              ? t("provider.sijainnit.puomiRangeError")
              : null
          }
        />
      )}
    </Box>
  );
}
